// apps/web/src/lib/useCachedResource.ts
// Stale-while-revalidate for a page's payload: paint the last thing we saw,
// fetch behind it, replace when the answer lands.
//
// Today already worked this way; Progress and AddFood fetched cold on every
// visit, so every tab switch was blank → skeleton → pop. This is that same
// pattern, once, for anyone who wants it.
//
// Two rules the callers have to respect:
//
//  - Only snapshot what is harmless when it is a few seconds old. A weight
//    chart from this morning is fine. A calorie budget is not — a stale budget
//    is a wrong number that looks exactly like a right one.
//  - The cache key must name everything the payload depends on (range, date,
//    sort order). A key that is too coarse shows one request's answer for
//    another's question.
import { useCallback, useEffect, useRef, useState } from "react";
import { readSnapshot, writeSnapshot } from "./snapshot";

export interface CachedResource<T> {
  /** Cached first, then fresh. Null only when there was no snapshot yet. */
  data: T | null;
  /** True while showing a snapshot that this mount has not yet revalidated. */
  stale: boolean;
  /** Set only when the fetch failed. Any snapshot stays on screen alongside it
   *  — blanking a page the user was reading is worse than showing it a minute
   *  old, as long as the failure is visible. */
  error: string | null;
  reload: () => Promise<void>;
  /** For local edits that shouldn't wait for a round trip. */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export interface CachedResourceOptions {
  /** Ignore snapshots older than this. Omit to accept any age. */
  maxAgeMs?: number;
  /** Skip persisting (Today only snapshots the current day, for instance). */
  persist?: boolean;
}

export function useCachedResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  { maxAgeMs, persist = true }: CachedResourceOptions = {},
): CachedResource<T> {
  const [data, setData] = useState<T | null>(() => readSnapshot<T>(key, maxAgeMs));
  const [stale, setStale] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetches are ordered by a token rather than cancelled: a slow response for
  // an old key must never overwrite a fast one for the current key.
  const latest = useRef(0);
  // The fetcher is usually an inline closure, so a new identity every render.
  // Holding it in a ref keeps `reload` stable and the effect keyed on `key`.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(async () => {
    const token = ++latest.current;
    try {
      const fresh = await fetcherRef.current();
      if (token !== latest.current) return;
      setData(fresh);
      setStale(false);
      setError(null);
      if (persist) writeSnapshot(key, fresh);
    } catch (e) {
      if (token !== latest.current) return;
      setError((e as Error).message);
      setStale(false);
    }
  }, [key, persist]);

  useEffect(() => {
    // Repaint from whatever we have for the NEW key before the fetch resolves,
    // so switching a range shows that range's last answer instead of a skeleton
    // — or, worse, the previous range's numbers under the new label.
    setData(readSnapshot<T>(key, maxAgeMs));
    setStale(true);
    setError(null);
    void reload();
    // maxAgeMs is a constant per call site; keying on `reload` covers key too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  return { data, stale, error, reload, setData };
}
